import React, { useEffect, useRef, useState } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { css } from '@emotion/react';
import theme from '@styles/theme';
import {
  DeleteBtn,
  TextFieldUploadOnIcon,
  TextFieldUploadIcon,
} from 'public/assets';
import Portal from '@components/commons/portal/Portal';
import { useChatPlayContext } from '@provider/ChatPlayProvider';

import { Choice } from '@/customTypes/chatStory';

interface IChoiceInput {
  choice: Choice;
}

const ChoiceInput = ({ choice }: IChoiceInput) => {
  const { t } = useTranslation();
  const [choiceText, setChoiceText] = useState<string>('');
  const { onChoice } = useChatPlayContext();
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    window.addEventListener('keypress', handleKeyPress);
    return () => {
      setChoiceText('');
      window.removeEventListener('keypress', handleKeyPress);
    };
  }, [choice]);

  const handleKeyPress = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      inputRef.current?.value?.length && onMakeChoice();
    }
  };

  const onMakeChoice = () => {
    const choiceText = inputRef.current?.value || '';
    if (choiceText.length) {
      const leftUserInputText = choice.options?.leftUserInputText;
      const rightUserInputText = choiceText;

      onChoice({
        choiceName: choice.choiceName || '',
        choiceUserInput: {
          leftUserInputText,
          rightUserInputText,
        },
      });

      setChoiceText('');
    }
  };

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    setChoiceText(value);
  };

  if (!choice || choice.options?.layoutType !== 'UserInputChoice') {
    return null;
  }

  const onDelete = () => {
    setChoiceText('');
  };

  return (
    <Portal>
      <div css={container(choiceText.length > 0)} ref={containerRef}>
        <div className="bottom-container">
          <div className="input-container">
            <input
              ref={inputRef}
              className="input"
              placeholder={t('chat_screen_description_input_quotes_directly')}
              value={choiceText}
              onChange={onChange}
            />
            <DeleteBtn
              width={24}
              height={24}
              className="delete-icon"
              onClick={onDelete}
            />
          </div>

          <div className="send-icon" onClick={onMakeChoice}>
            {choiceText.length > 0 ? (
              <TextFieldUploadOnIcon />
            ) : (
              <TextFieldUploadIcon />
            )}
          </div>
        </div>
      </div>
    </Portal>
  );
};

const container = (isInputText: boolean) => css`
  position: fixed;
  overflow: hidden;
  width: 100%;
  max-width: ${theme.maxWidth};
  background-color: ${theme.colors.white};
  bottom: 0;
  left: 50%;
  transform: translate(-50%, 0);
  padding: 1em;
  border: 1px solid ${theme.colors.gray300};

  .bottom-container {
    display: fixed;
    align-items: center;
    justify-content: space-between;
  }

  .input-container {
    display: fixed;
    align-items: center;
    width: 92%;
    position: relative;

    .input {
      width: 100%;
      font-style: normal;
      font-weight: 400;
      font-size: 1.25em;
      color: ${theme.colors.gray900};
      display: flex;
      align-items: center;
      border: none;
      padding: 0.5em 1em 0.5em 0.5em;
    }

    .delete-icon {
      visibility: ${isInputText ? 'visible' : 'hidden'};
      cursor: pointer;
      margin-left: -8px;
      margin-right: 8px;
    }

    .length {
      height: 24px;
      margin-right: 10px;
    }

    @media screen and (max-width: ${theme.devices.mobile}) {
      width: 80%;
    }

    @media screen and (max-width: 500px) {
      width: 70%;
    }

    input::placeholder {
      color: ${theme.colors.gray400};
    }
  }

  .error {
    display: flex;
    gap: 6px;
    margin-left: 1em;
    font-style: normal;
    font-weight: 400;
    font-size: 12px;
    color: ${theme.colors.danger600};
  }

  .send-icon {
    margin-left: 13px;
    cursor: pointer;
  }
`;
export default ChoiceInput;
